import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateUserList,
  resetError,
} from "./User.actions";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import ServerSideReactTable from 'commons/table/ServerSideReactTable';
import ResetListState from "components/ResetListState";
import Tip from "components/Tip";
const DEFAULT_PAGE_SIZE = 15;
import { FilterTypes, ColumnTypes } from 'commons/table/Constants';
import ReactBadge from "react-bootstrap/Badge";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import { getErrorMessage } from "services/UtilityService";
import EditUser from "components/User/EditUser";
import RegisterNewUser from "components/User/RegisterNewUser";
import DisableUser from "components/User/DisableUser";
import DeleteUser from "components/User/DeleteUser";
import { showNotification } from "services/NotificationService";

const Badge = (props) => (
  <div>
    <ReactBadge {...props}>{props.children}</ReactBadge>
  </div>
);
const spaceyStyle = {
  letterSpacing: "0.6px",
};

const UserList = () => {
  const dispatch = useDispatch();
  const [roles, setRoles] = useState(null);
  const isMountedRef = useRef(true);

  const { data, pages, totalElements, listState, loading, isError } = useSelector(state => {
    const userList = state.userList || {};
    return {
      data: userList.data || [],
      pages: userList.pages || 0,
      totalElements: userList.totalElements !== undefined ? userList.totalElements : "Loading ...",
      loading: userList.loading,
      isError: userList.isError,
      listState: userList.listState || {
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE,
        sorted: [],
        filtered: [],
        resized: [],
        expanded: {},
      },
    };
  });

  const updateData = useCallback((state) => {
    dispatch(updateUserList(
      state.pageSize,
      state.page,
      state.sorted,
      state.filtered,
      state.resized,
      state.expanded
    ));
  }, [dispatch]);

  const handler = useCallback(() => {
    updateData(listState);
  }, [updateData, listState]);

  const listRole = useCallback(() => {
    sendAuthenticatedAsyncRequest(
      `/user/list_roles`,
      "GET",
      null,
      (r) => {
        if (isMountedRef.current) {
          setRoles(r.data);
        }
      },
      (r) => {
        if (isMountedRef.current) {
          setRoles(null);
          showNotification({
            title: "Error fetching roles",
            message: getErrorMessage(r.data) || getErrorMessage("SOMETHING_WENT_WRONG"),
            position: "bl",
            type: "error"
          });
        }
      }
    );
  }, []);

  useEffect(() => {
    // Initialize data on mount with current listState
    const initialState = {
      page: listState.page,
      pageSize: listState.pageSize,
      sorted: listState.sorted,
      filtered: listState.filtered,
      resized: listState.resized,
      expanded: listState.expanded
    };
    updateData(initialState);
    listRole();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isError) {
      showNotification({
        title: "Error Loading Data!",
        message: "Something went Wrong.",
        position: "bl",
        type: "error"
      });
      dispatch(resetError());
    }
  }, [isError, dispatch]);

  useEffect(() => {
    if (listState.page !== 0 && listState.page >= pages && pages > 0) {
      updateData({ ...listState, page: 0 });
    }
  }, [listState, pages, updateData]);

  const onUpdateTable = useCallback((filterValues, sortBy, pageSize, pageNo) => {
    const newState = {
      page: pageNo,
      pageSize: pageSize,
      sorted: sortBy ? sortBy : [],
      filtered: filterValues,
      resized: [],
      expanded: {}
    };
    updateData(newState);
  }, [updateData]);

  const renderAllRoles = useCallback((roles) => {
    if (roles) {
      return (
        <span
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            rowGap: "4px"
          }}
        >
          {" "}
          {roles
            ? roles.split(",").map((role, counter) => (
              <Badge
                key={counter}
                variant="primary"
                id={`${role}-${counter}`}
                pill
                style={{
                  ...spaceyStyle,
                  marginLeft: "0.5rem",
                  fontSize: "0.7rem",
                }}
              >
                {role}
              </Badge>
            ))
            : ""}{" "}
        </span>
      )
    }
    return <span></span>;
  }, []);

  const renderAllActions = useCallback((rowData) => {
    if (rowData) {
      return (
        <span
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-evenly",
          }}
        >
          {" "}
          <EditUser
            user={rowData}
            showButton={true}
            roles={roles}
            handler={handler}
          ></EditUser>
          <DisableUser userInfoId={rowData.userInfoId} handler={handler}></DisableUser> <DeleteUser userInfoId={rowData.userInfoId} handler={handler}></DeleteUser>{" "}
        </span>
      )
    }
  }, [roles, handler]);

  const columns = useMemo(() => [
    {
      header: "Name",
      accessorKey: "userName",
      width: 200,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
      show: true,
    },
    {
      header: "Email",
      accessorKey: "email",
      width: 350,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
      show: true,
    },
    {
      header: "IP Address",
      accessorKey: "ipAddress",
      width: 200,
      enableSorting: false,
      enableColumnFilter: false,
      columnType: ColumnTypes.Text,
      show: false,
    },
    {
      header: "User Info Id",
      accessorKey: "userInfoId",
      width: 200,
      enableSorting: false,
      enableColumnFilter: false,
      columnType: ColumnTypes.Text,
      show: false,
    },
    {
      header: "Roles",
      accessorKey: "rolesList",
      width: 350,
      enableSorting: false,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      show: true,
      customComponent: (value) => {
        return renderAllRoles(value)
      }
    },
    {
      header: "Actions",
      accessorKey: "action",
      width: 200,
      enableSorting: false,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      show: true,
      customComponent: (value, row) => {
        return renderAllActions(row.original)
      }
    },
  ], [renderAllRoles, renderAllActions]);

  return (
    <Container className="list-container" fluid>
      <Row>
        <Col md={12}>
          <div className="codex-version-display-bar">
            <span style={{ float: "left" }}>
              {" "}
              Total Users:{" "}
              <strong id="user-total-count">{totalElements}</strong>
            </span>
            <strong style={{ float: "center" }}>Users List</strong>
            <span className="tiny-bottom-spacer">
              <RegisterNewUser showButton={true} roles={roles} handler={handler}></RegisterNewUser>
              <ResetListState
                onReset={() =>
                  updateData(
                    {
                      ...listState,
                      filtered: [],
                      sorted: [],
                    }
                  )
                }
              />
            </span>
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ServerSideReactTable
            columnDefination={columns}
            data={data}
            overrideRowId={'userInfoId'}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            currentPage={listState.page}
            sorted={listState.sorted}
            filters={listState.filtered}
            totalPages={pages}
            totalRecords={totalElements === "loading" ? 0 : totalElements}
            loading={loading}
            enableMultiRowSelection={false}
            onUpdateTableData={onUpdateTable}
          />
          <Tip>
            Tip: Hold shift when sorting to multi-sort and disable sort!
          </Tip>
        </Col>
      </Row>
    </Container>
  );
}

export default UserList;
